/**
 * Identyfikator rozmowy z Copilotem (Fala 10): idempotencja działań w obrębie rozmowy — powtórzone polecenie (albo
 * ponowienie po błędzie sieci) nie tworzy drugiego klienta, zadania czy spotkania. Nowy przy pierwszej wiadomości
 * rozmowy (po „Nowa rozmowa”), zapamiętany w sessionStorage karty (rozmowa zostaje po odświeżeniu strony). Ponowienie
 * pierwszej wiadomości („Spróbuj ponownie”) zachowuje identyfikator.
 */

const KEY = 'fos.copilot.conversation.v1';

interface Stored {
  id: string;
  /** pierwsza wiadomość rozmowy (ponowienie tej samej = ta sama rozmowa) */
  first: string;
}

let current: Stored | null = null;

function storage(): Storage | null {
  try {
    return typeof window !== 'undefined' ? window.sessionStorage : null;
  } catch {
    return null;
  }
}

function newId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') return crypto.randomUUID();
  // zapas bez crypto.randomUUID (stare przeglądarki): UUID v4 z Math.random — tylko klucz idempotencji
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = Math.floor(Math.random() * 16);
    return (c === 'x' ? r : (r % 4) + 8).toString(16);
  });
}

function load(): Stored | null {
  try {
    const raw = storage()?.getItem(KEY);
    const v = raw ? (JSON.parse(raw) as Partial<Stored>) : null;
    return v && typeof v.id === 'string' && typeof v.first === 'string' ? { id: v.id, first: v.first } : null;
  } catch {
    return null;
  }
}

/** Identyfikator dla historii wysyłanej do Copilota: pierwsza (nowa) wiadomość osoby = nowa rozmowa. */
export function conversationIdFor(history: readonly { role: string; content: string }[]): string {
  const users = history.filter((t) => t.role === 'user');
  const first = users[0]?.content ?? '';
  current ??= load();
  if (!current || (users.length <= 1 && current.first !== first)) current = { id: newId(), first };
  try {
    storage()?.setItem(KEY, JSON.stringify(current));
  } catch {
    // bez pamięci sesji — identyfikator do końca tej strony
  }
  return current.id;
}

/** testy */
export function resetConversationId(): void {
  current = null;
  try {
    storage()?.removeItem(KEY);
  } catch {
    // bez pamięci sesji
  }
}

/**
 * Wznowienie zapisanej rozmowy (Fala 10, historia promptów): kolejne wiadomości idą z identyfikatorem tej rozmowy
 * (serwer dopisuje je do niej). `first` = pierwsze polecenie rozmowy.
 */
export function resumeConversationId(id: string, first: string): void {
  current = { id, first };
  try {
    storage()?.setItem(KEY, JSON.stringify(current));
  } catch {
    // bez pamięci sesji — identyfikator do końca tej strony
  }
}
